import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Header } from "@/pages/CollegePredictorPage";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  GraduationCap, Plus, Pencil, Trash2, Eye, MousePointerClick, Users, Power,
  Download, Search, Send, Loader2, X,
} from "lucide-react";

const EMPTY = { name: "", program: "", application_link: "", deadline: "", description: "", image_url: "", active: true, exam: "", streams: [], tags: [] };

export default function AdminCollegesPage() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/recommended-colleges").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async (id) => { await api.post(`/admin/recommended-colleges/${id}/toggle`); load(); };
  const remove = async (id) => {
    if (!window.confirm("Delete this listing permanently?")) return;
    await api.delete(`/admin/recommended-colleges/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-8 py-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <Header icon={GraduationCap} eyebrow="Admin · Recommended Colleges"
          title="Manage college listings" desc="Add, edit, activate and track applications." />
        <CollegeFormDialog onSaved={load} />
      </div>

      <div className="grid gap-4">
        {list.map((c) => (
          <motion.div key={c.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            data-testid="admin-college-row"
            className={`rounded-2xl border p-5 ${c.active ? "border-white/8 bg-[#131C2E]" : "border-white/5 bg-[#0F1626] opacity-70"}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex gap-4 min-w-0">
                {c.image_url ? (
                  <img src={c.image_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }}
                    className="h-16 w-24 rounded-lg object-cover shrink-0" />
                ) : (
                  <div className="flex h-16 w-24 items-center justify-center rounded-lg bg-white/5 shrink-0">
                    <GraduationCap className="h-6 w-6 text-slate-500" />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-display font-semibold text-white truncate">{c.name}</p>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${c.active ? "bg-emerald-400/15 text-emerald-300" : "bg-slate-600/40 text-slate-300"}`}>
                      {c.active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <p className="text-sm text-cyan-300">{c.program}</p>
                  <p className="text-xs text-slate-400">Deadline: {c.deadline || "—"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="college-toggle-btn" onClick={() => toggle(c.id)} title="Activate/Deactivate"
                  className="rounded-lg border border-white/10 p-2 text-slate-300 hover:text-white"><Power className="h-4 w-4" /></button>
                <CollegeFormDialog college={c} onSaved={load} trigger={
                  <button className="rounded-lg border border-white/10 p-2 text-slate-300 hover:text-white"><Pencil className="h-4 w-4" /></button>
                } />
                <button data-testid="college-delete-btn" onClick={() => remove(c.id)}
                  className="rounded-lg border border-white/10 p-2 text-rose-300 hover:text-rose-200"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Metric icon={Eye} label="Viewed" value={c.views} />
              <Metric icon={MousePointerClick} label="Clicked Apply" value={c.clicks} />
              <Metric icon={Users} label="Applications" value={c.applications} />
              <div className="ml-auto flex gap-2">
                <ApplicationsDialog college={c} />
                <NotifyDialog college={c} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

const Metric = ({ icon: Icon, label, value }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1.5 text-xs text-slate-300">
    <Icon className="h-3.5 w-3.5 text-cyan-400" /> {value} <span className="text-slate-500">{label}</span>
  </span>
);

function CollegeFormDialog({ college, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const editing = !!college;

  useEffect(() => {
    if (open) setForm(college ? {
      name: college.name, program: college.program, application_link: college.application_link || "",
      deadline: college.deadline || "", description: college.description || "", image_url: college.image_url || "",
      active: college.active, exam: college.exam || "", streams: college.streams || [], tags: college.tags || [],
    } : EMPTY);
  }, [open, college]);

  const save = async () => {
    if (!form.name || !form.program) { toast.info("Name & program are required"); return; }
    setLoading(true);
    try {
      const payload = { ...form,
        streams: typeof form.streams === "string" ? form.streams.split(",").map((s) => s.trim()).filter(Boolean) : form.streams,
        tags: typeof form.tags === "string" ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : form.tags };
      if (editing) await api.put(`/admin/recommended-colleges/${college.id}`, payload);
      else await api.post("/admin/recommended-colleges", payload);
      toast.success(editing ? "Updated" : "College added");
      setOpen(false); onSaved();
    } catch { toast.error("Save failed"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <button data-testid="add-college-btn"
            className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2.5 text-sm font-bold text-[#041016] transition-transform hover:scale-105">
            <Plus className="h-4 w-4" /> Add College
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-[#131C2E] border-white/10 text-white max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "Add"} college / university</DialogTitle>
          <DialogDescription className="text-slate-400">Fill in the listing details students will see and apply to.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <F label="Name" testid="cf-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <F label="Course / Program" testid="cf-program" value={form.program} onChange={(v) => setForm({ ...form, program: v })} />
          <F label="Application Link" testid="cf-link" value={form.application_link} onChange={(v) => setForm({ ...form, application_link: v })} />
          <F label="Application Deadline" testid="cf-deadline" value={form.deadline} onChange={(v) => setForm({ ...form, deadline: v })} placeholder="e.g. 30 Sep 2026" />
          <F label="Logo / Image URL" testid="cf-image" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} />
          <div>
            <label className="text-sm text-slate-300">Description</label>
            <textarea data-testid="cf-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Exam (for AI match)" testid="cf-exam" value={form.exam} onChange={(v) => setForm({ ...form, exam: v })} placeholder="JEE Main / NEET / CUET" />
            <F label="Streams (comma-sep)" testid="cf-streams" value={Array.isArray(form.streams) ? form.streams.join(", ") : form.streams} onChange={(v) => setForm({ ...form, streams: v })} placeholder="Science, Commerce" />
          </div>
          <F label="Tags (comma-sep)" testid="cf-tags" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(v) => setForm({ ...form, tags: v })} placeholder="Engineering, Management" />
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-cyan-400 h-4 w-4" data-testid="cf-active" />
            Active (visible to students)
          </label>
          <button data-testid="cf-save-btn" onClick={save} disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-400 px-6 py-3 text-sm font-bold text-[#041016] disabled:opacity-50">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ApplicationsDialog({ college }) {
  const [open, setOpen] = useState(false);
  const [apps, setApps] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("All");

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (status !== "All") params.status = status;
    api.get(`/admin/recommended-colleges/${college.id}/applications`, { params }).then((r) => setApps(r.data)).catch(() => {});
  };
  useEffect(() => { if (open) load(); }, [open, q, status]);

  const exportCsv = async () => {
    const token = localStorage.getItem("pf_token");
    const res = await fetch(`${API}/admin/recommended-colleges/${college.id}/applications/export`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${college.name.replace(/\s+/g, "_")}_applications.csv`;
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  const STATUSES = ["Applied", "Under Review", "Shortlisted", "Accepted", "Rejected", "Waitlisted"];
  const changeStatus = async (appId, newStatus) => {
    try {
      const { data } = await api.put(`/admin/applications/${appId}/status`, { status: newStatus });
      setApps((prev) => prev.map((a) => (a.id === appId ? { ...a, status: newStatus } : a)));
      toast.success(`Status updated to "${newStatus}"${data.whatsapp_sent ? " · WhatsApp sent" : " · student notified"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update status"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="view-applications-btn"
          className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-1.5 text-xs font-bold text-cyan-300">
          <Users className="h-3.5 w-3.5" /> Applications
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#131C2E] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Applications · {college.name}</DialogTitle>
          <DialogDescription className="text-slate-400">Search, filter, update status and export applicants for this listing.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
            <input data-testid="app-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / email / mobile"
              className="w-full rounded-xl border border-white/10 bg-white/[0.03] pl-9 pr-4 py-2 text-sm text-white outline-none focus:border-cyan-400/50" />
          </div>
          <select data-testid="app-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
            className="rounded-xl border border-white/10 bg-[#0F1626] px-3 py-2 text-sm text-white">
            {["All", ...STATUSES].map((s) => <option key={s}>{s}</option>)}
          </select>
          <button data-testid="export-csv-btn" onClick={exportCsv}
            className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-400 px-3 py-2 text-sm font-bold text-[#041016]">
            <Download className="h-4 w-4" /> Export
          </button>
        </div>
        <div className="mt-3 overflow-x-auto">
          {apps.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">No applications yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="pb-2 font-medium">Name</th><th className="pb-2 font-medium">Mobile</th>
                  <th className="pb-2 font-medium">Email</th><th className="pb-2 font-medium">Program</th>
                  <th className="pb-2 font-medium">Date</th><th className="pb-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((a) => (
                  <tr key={a.id} data-testid="application-table-row" className="border-t border-white/5">
                    <td className="py-2 text-white">{a.student_name}</td>
                    <td className="py-2 text-slate-400">{a.mobile}</td>
                    <td className="py-2 text-slate-400">{a.email}</td>
                    <td className="py-2 text-slate-400">{a.program}</td>
                    <td className="py-2 text-slate-500">{new Date(a.applied_at).toLocaleDateString("en-IN")}</td>
                    <td className="py-2">
                      <select data-testid="app-status-select" value={a.status || "Applied"}
                        onChange={(e) => changeStatus(a.id, e.target.value)}
                        className="rounded-lg border border-white/10 bg-[#0F1626] px-2 py-1 text-xs text-cyan-200 outline-none focus:border-cyan-400/50">
                        {STATUSES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NotifyDialog({ college }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [channel, setChannel] = useState("in-app");
  const [loading, setLoading] = useState(false);

  const send = async () => {
    if (!message.trim()) { toast.info("Write a message"); return; }
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/recommended-colleges/${college.id}/notify`, { message, channel });
      toast.success(data.message);
      setOpen(false); setMessage("");
    } catch { toast.error("Failed to send"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="notify-applicants-btn"
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 text-xs font-bold text-slate-200">
          <Send className="h-3.5 w-3.5" /> Notify
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#131C2E] border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-display">Notify applicants · {college.name}</DialogTitle>
          <DialogDescription className="text-slate-400">Send an update to everyone who applied to this listing.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="flex gap-2">
            {["in-app", "push", "whatsapp"].map((ch) => (
              <button key={ch} onClick={() => setChannel(ch)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold capitalize ${channel === ch ? "bg-cyan-400 text-[#041016]" : "bg-white/5 text-slate-400"}`}>{ch}</button>
            ))}
          </div>
          {channel !== "in-app" && (
            <p className="rounded-lg border border-amber-400/20 bg-amber-400/5 px-3 py-2 text-xs text-amber-300">
              {channel === "whatsapp" ? "WhatsApp requires user consent & Twilio config — sent as in-app in demo mode." : "Push requires Firebase config — sent as in-app in demo mode."}
            </p>
          )}
          <textarea data-testid="notify-message" rows={4} value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder="e.g. Reminder: application deadline is approaching!"
            className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50" />
          <button data-testid="notify-send-btn" onClick={send} disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-400 px-6 py-3 text-sm font-bold text-[#041016] disabled:opacity-50">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Send notification</>}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const F = ({ label, testid, value, onChange, placeholder }) => (
  <div>
    <label className="text-sm text-slate-300">{label}</label>
    <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="mt-1 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50" />
  </div>
);
